package ai.wagemix.web.components

import ai.wagemix.web.theme.WMChartHeightCompactVar
import ai.wagemix.web.theme.WMChartHeightVar
import ai.wagemix.web.theme.WMOpacity
import ai.wagemix.web.theme.WMPreviewChartHeightVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.fsryan.charts.models.embed.FSChartsAnimationSpec
import com.fsryan.charts.models.embed.FSChartsAxisLabelPositionSpec
import com.fsryan.charts.models.embed.FSChartsAxisPlacementSpec
import com.fsryan.charts.models.embed.FSChartsAxisSpec
import com.fsryan.charts.models.embed.FSChartsCartesianAxisSpec
import com.fsryan.charts.models.embed.FSChartsCartesianChartSpec
import com.fsryan.charts.models.embed.FSChartsCartesianLayerSpec
import com.fsryan.charts.models.embed.FSChartsColorRole
import com.fsryan.charts.models.embed.FSChartsDashSpec
import com.fsryan.charts.models.embed.FSChartsDependentAxisRoleSpec
import com.fsryan.charts.models.embed.FSChartsDomainSpec
import com.fsryan.charts.models.embed.FSChartsDownsamplingSpec
import com.fsryan.charts.models.embed.FSChartsFillBaselineSpec
import com.fsryan.charts.models.embed.FSChartsFillPaintSpec
import com.fsryan.charts.models.embed.FSChartsFillSpec
import com.fsryan.charts.models.embed.FSChartsFormatterSpec
import com.fsryan.charts.models.embed.FSChartsGradientAnchorSpec
import com.fsryan.charts.models.embed.FSChartsInteractionSpec
import com.fsryan.charts.models.embed.FSChartsLabelOverlapSpec
import com.fsryan.charts.models.embed.FSChartsLegendLayout
import com.fsryan.charts.models.embed.FSChartsLegendSide
import com.fsryan.charts.models.embed.FSChartsLegendSpec
import com.fsryan.charts.models.embed.FSChartsLineCurve
import com.fsryan.charts.models.embed.FSChartsLineLayerSpec
import com.fsryan.charts.models.embed.FSChartsLineRegionSpec
import com.fsryan.charts.models.embed.FSChartsLineRegionStyleSpec
import com.fsryan.charts.models.embed.FSChartsLineStyleSpec
import com.fsryan.charts.models.embed.FSChartsMarkerShapeSpec
import com.fsryan.charts.models.embed.FSChartsMarkerSpec
import com.fsryan.charts.models.embed.FSChartsMarkerVisibilitySpec
import com.fsryan.charts.models.embed.FSChartsOrientationSpec
import com.fsryan.charts.models.embed.FSChartsReferenceBandLayerSpec
import com.fsryan.charts.models.embed.FSChartsReferenceLabelPositionSpec
import com.fsryan.charts.models.embed.FSChartsScaleSpec
import com.fsryan.charts.models.embed.FSChartsSeriesColor
import com.fsryan.charts.models.embed.FSChartsSnapModeSpec
import com.fsryan.charts.models.embed.FSChartsStackModeSpec
import com.fsryan.charts.models.embed.FSChartsStrokeCapSpec
import com.fsryan.charts.models.embed.FSChartsStrokeJoinSpec
import com.fsryan.charts.models.embed.FSChartsTickDirectionSpec
import com.fsryan.charts.models.embed.FSChartsTickSpec
import com.fsryan.charts.models.embed.kxs
import com.fsryan.charts.web.components.FSChartsChart
import com.fsryan.foundation.web.embed.FSWasmEmbedLoading
import com.fsryan.foundation.web.embed.FSWasmEmbedSize
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.aspectRatio
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlin.math.ceil
import kotlin.math.floor
import kotlin.math.log10
import kotlin.math.pow
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Div

/**
 * One year of weighted percentile wages for the band chart. `null` where the data is missing.
 */
interface WMBandChartPoint {
    val year: Int
    val p10: Double?
    val p25: Double?
    val median: Double?
    val p75: Double?
    val p90: Double?

    companion object
}

/**
 * What the band chart draws: the weighted percentile series by year, which years are
 * projected (every year after [lastActualYear]), and whether the figures are annual or
 * hourly. Presenters build it; the web layer turns it into an fscharts spec.
 */
interface WMBandChartModel {
    val points: List<WMBandChartPoint>
    /** The last year with real BLS data; later years are projected. `null` when it is unknown (a sample model). */
    val lastActualYear: Int?
    val hourly: Boolean

    companion object
}

fun WMBandChartPoint.Companion.of(
    year: Int,
    p10: Double?,
    p25: Double?,
    median: Double?,
    p75: Double?,
    p90: Double?
): WMBandChartPoint = WMBandChartPointData(year, p10, p25, median, p75, p90)

fun WMBandChartModel.Companion.of(
    points: List<WMBandChartPoint>,
    lastActualYear: Int?,
    hourly: Boolean
): WMBandChartModel = WMBandChartModelData(points, lastActualYear, hourly)

private data class WMBandChartPointData(
    override val year: Int,
    override val p10: Double?,
    override val p25: Double?,
    override val median: Double?,
    override val p75: Double?,
    override val p90: Double?
): WMBandChartPoint

private data class WMBandChartModelData(
    override val points: List<WMBandChartPoint>,
    override val lastActualYear: Int?,
    override val hourly: Boolean
): WMBandChartModel

/** The share of the year span added before the first year and after the last, so edge markers are not clipped. */
private const val YEAR_AXIS_PADDING = 0.02

/**
 * Where the projected region starts, relative to the last actual year: halfway to the first projected year on the
 * linear year axis, so the region begins after the last real point and before the first projected one.
 */
private const val PROJECTED_REGION_OFFSET = 0.5

/** The room above the highest value shown, as a share of it, so its line never touches the plot's top edge. */
private const val VALUE_AXIS_HEADROOM = 0.04

/** The most steps the value axis is divided into; with the step factors below it always has at least three. */
private const val VALUE_AXIS_MAX_STEPS = 5

/** The round steps of the value axis, times a power of ten. */
private val VALUE_AXIS_STEP_FACTORS = listOf(1.0, 2.0, 2.5, 5.0)

/** The width of each percentile line, the edges of the bands. */
private const val PERCENTILE_LINE_WIDTH_DP = 1F

private const val REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"

/** Series names; also what `hiddenSeries` and the legend use. */
object WMBandChartSeries {
    const val P90 = "P90"
    const val P75 = "P75"
    const val MEDIAN = "Median"
    const val P25 = "P25"
    const val P10 = "P10"
    const val PROJECTED = "Projected"
    /** The reference band behind the projected years; it has no legend item. */
    const val PROJECTED_BAND = "Projected years"
}

/**
 * The fscharts spec of the redesign's band chart (redesign-spec §2 "fs-charts mapping"): two
 * primary-coloured bands around a 2.5dp median line with circle markers, the projected years
 * dashed in the secondary colour and labelled "Projected", a linear x axis ticked at each year, a
 * currency y axis, hover/tap tooltip with crosshair and a bottom legend.
 *
 * Each band is two 1dp percentile lines with the area between them filled (P90 down to P10, P75
 * down to P25), not a range layer: fs-charts writes a range's tooltip row as "low – high", and the
 * tooltip lists every percentile on its own row. The lines come in tooltip order: P90, P75,
 * median, P25, P10.
 *
 * The projected region starts halfway between the last actual year
 * ([WMBandChartModel.lastActualYear]) and the first projected year, and runs to the end of the
 * plot. Behind the data a secondary-coloured reference band spans the same region, unlabelled:
 * fs-charts keeps a band's label inside the band, so a label wider than the band (one projected
 * year on a phone) runs off the chart; the legend's "Projected" item names it instead. With no
 * projected point there is neither region nor band.
 *
 * The value axis runs from 0 to a round value above the highest value shown ([valueTicks]), with
 * a tick at each step, so the top band never touches the plot's top edge and the top tick is drawn
 * whatever the chart's height. Hidden series do not count, so hiding a band rescales the axis.
 *
 * [animated] draws the chart on and moves it between data sets.
 */
object WMBandChartSpec {

    fun of(
        model: WMBandChartModel,
        title: String = "",
        hiddenSeries: List<String> = emptyList(),
        animated: Boolean = false
    ): FSChartsCartesianChartSpec {
        val x = model.points.map { it.year.toDouble() }
        val lastActualYear = model.lastActualYear
        val projectedFrom = if (lastActualYear != null && model.points.any { it.year > lastActualYear }) {
            lastActualYear + PROJECTED_REGION_OFFSET
        } else {
            null
        }

        val median = FSChartsLineLayerSpec.kxs(
            dependentAxisRole = FSChartsDependentAxisRoleSpec.Primary,
            labels = model.points.map { null },
            regions = listOfNotNull(projectedFrom?.let { projectedLineRegion(it) }),
            seriesName = WMBandChartSeries.MEDIAN,
            style = FSChartsLineStyleSpec.kxs(
                cap = FSChartsStrokeCapSpec.Round,
                color = FSChartsSeriesColor.Role(FSChartsColorRole.Primary),
                curve = FSChartsLineCurve.Linear,
                dash = FSChartsDashSpec.Solid,
                downsampling = FSChartsDownsamplingSpec.None,
                fill = null,
                join = FSChartsStrokeJoinSpec.Round,
                marker = FSChartsMarkerSpec.kxs(
                    fillColor = null,
                    filled = true,
                    outlineColor = null,
                    outlineWidthDp = null,
                    outlined = false,
                    shape = FSChartsMarkerShapeSpec.Circle,
                    sizeDp = 6F
                ),
                markerVisibility = FSChartsMarkerVisibilitySpec.All,
                pointLabels = null,
                strokeWidthDp = 2.5F,
                valueColor = null
            ),
            x = x,
            y = model.points.map { it.median }
        )

        val layers: List<FSChartsCartesianLayerSpec> = listOfNotNull(
            projectedFrom?.let { from -> projectedBand(from = from, to = x.max() + PROJECTED_REGION_OFFSET) },
            percentileLine(
                name = WMBandChartSeries.P90,
                x = x,
                y = model.points.map { it.p90 },
                fill = bandFill(down = WMBandChartSeries.P10, opacity = WMOpacity.BAND_OUTER),
                projectedFrom = projectedFrom
            ),
            percentileLine(
                name = WMBandChartSeries.P75,
                x = x,
                y = model.points.map { it.p75 },
                fill = bandFill(down = WMBandChartSeries.P25, opacity = WMOpacity.BAND_INNER),
                projectedFrom = projectedFrom
            ),
            median,
            percentileLine(
                name = WMBandChartSeries.P25,
                x = x,
                y = model.points.map { it.p25 },
                fill = null,
                projectedFrom = projectedFrom
            ),
            percentileLine(
                name = WMBandChartSeries.P10,
                x = x,
                y = model.points.map { it.p10 },
                fill = null,
                projectedFrom = projectedFrom
            )
        )

        return FSChartsCartesianChartSpec.kxs(
            animation = if (animated) FSChartsAnimationSpec.kxs(durationMillis = null, enter = true, update = true) else null,
            barBaseline = 0.0,
            barSpacing = null,
            barStackMode = FSChartsStackModeSpec.None,
            barTotalLabels = null,
            dependentAxis = valueAxis(model.hourly, valueTicks(highestShown(model, hiddenSeries), wholeDollars = model.hourly)),
            frame = true,
            hiddenSeries = hiddenSeries,
            independentAxis = yearAxis(x),
            interaction = FSChartsInteractionSpec.kxs(
                badges = false,
                crosshair = true,
                hover = true,
                keyboard = true,
                scrub = true,
                snapMode = FSChartsSnapModeSpec.NearestIndependent,
                tap = true,
                tooltip = true
            ),
            layers = layers,
            legend = FSChartsLegendSpec.kxs(layout = FSChartsLegendLayout.Row, side = FSChartsLegendSide.Bottom),
            lineStackMode = FSChartsStackModeSpec.None,
            orientation = FSChartsOrientationSpec.Vertical,
            rescaleOnHide = true,
            secondaryAxis = null,
            secondaryLegendSuffix = null,
            seriesCues = false,
            title = title,
            zoom = null
        )
    }

    // A band's edge: a thin primary line with no markers, dashed in the secondary colour over the projected years. The
    // upper edge of a band carries its fill, down to the lower edge.
    private fun percentileLine(
        name: String,
        x: List<Double>,
        y: List<Double?>,
        fill: FSChartsFillSpec?,
        projectedFrom: Double?
    ): FSChartsLineLayerSpec = FSChartsLineLayerSpec.kxs(
        dependentAxisRole = FSChartsDependentAxisRoleSpec.Primary,
        labels = y.map { null },
        regions = listOfNotNull(
            projectedFrom?.let { from ->
                FSChartsLineRegionSpec.kxs(
                    axis = FSChartsCartesianAxisSpec.Independent,
                    from = from,
                    inLegend = false,
                    label = WMBandChartSeries.PROJECTED,
                    style = FSChartsLineRegionStyleSpec.kxs(
                        color = FSChartsSeriesColor.Role(FSChartsColorRole.Secondary),
                        dash = FSChartsDashSpec.Pattern(intervalsDp = listOf(4F, 3F)),
                        fill = fill,
                        marker = null,
                        markerVisibility = null,
                        opacity = null,
                        pointLabels = null,
                        strokeWidthDp = null,
                        valueColor = null
                    ),
                    to = null
                )
            }
        ),
        seriesName = name,
        style = FSChartsLineStyleSpec.kxs(
            cap = FSChartsStrokeCapSpec.Butt,
            color = FSChartsSeriesColor.Role(FSChartsColorRole.Primary),
            curve = FSChartsLineCurve.Linear,
            dash = FSChartsDashSpec.Solid,
            downsampling = FSChartsDownsamplingSpec.None,
            fill = fill,
            join = FSChartsStrokeJoinSpec.Round,
            marker = null,
            markerVisibility = FSChartsMarkerVisibilitySpec.None,
            pointLabels = null,
            strokeWidthDp = PERCENTILE_LINE_WIDTH_DP,
            valueColor = null
        ),
        x = x,
        y = y
    )

    private fun bandFill(down: String, opacity: Float): FSChartsFillSpec = FSChartsFillSpec.kxs(
        anchor = FSChartsGradientAnchorSpec.Series,
        baseline = FSChartsFillBaselineSpec.Series(down),
        opacity = opacity,
        paint = FSChartsFillPaintSpec.Solid
    )

    // Behind the data layers. Its end lies past the last year; fs-charts draws only the part on the plot, so the band
    // runs to the plot's end whatever the axis padding. No label: fs-charts clamps a band label to the band, so one
    // wider than the band (one projected year on a phone, "Projecte") runs off the chart.
    private fun projectedBand(from: Double, to: Double): FSChartsReferenceBandLayerSpec = FSChartsReferenceBandLayerSpec.kxs(
        axis = FSChartsCartesianAxisSpec.Independent,
        color = FSChartsSeriesColor.Role(FSChartsColorRole.Secondary),
        dependentAxisRole = FSChartsDependentAxisRoleSpec.Primary,
        from = from,
        includeInDomain = false,
        label = null,
        labelPosition = FSChartsReferenceLabelPositionSpec.End,
        seriesName = WMBandChartSeries.PROJECTED_BAND,
        to = to
    )

    private fun projectedLineRegion(from: Double): FSChartsLineRegionSpec = FSChartsLineRegionSpec.kxs(
        axis = FSChartsCartesianAxisSpec.Independent,
        from = from,
        inLegend = true,
        label = WMBandChartSeries.PROJECTED,
        style = FSChartsLineRegionStyleSpec.kxs(
            color = FSChartsSeriesColor.Role(FSChartsColorRole.Secondary),
            dash = FSChartsDashSpec.Pattern(intervalsDp = listOf(6F, 4F)),
            fill = null,
            marker = null,
            markerVisibility = null,
            opacity = null,
            pointLabels = null,
            strokeWidthDp = null,
            valueColor = null
        ),
        to = null
    )

    // A linear axis, so a region can start between two years' points instead of covering a whole year's band.
    private fun yearAxis(years: List<Double>): FSChartsAxisSpec = FSChartsAxisSpec.kxs(
        domain = listOf(FSChartsDomainSpec.Padded(YEAR_AXIS_PADDING)),
        formatter = FSChartsFormatterSpec.Number(maxFractionDigits = 0, grouping = false),
        grid = false,
        gridDashed = true,
        labelPosition = FSChartsAxisLabelPositionSpec.Outside,
        labelRotation = 0F,
        line = true,
        minorGrid = false,
        minorTicks = FSChartsTickSpec.None,
        overlap = FSChartsLabelOverlapSpec.Skip,
        placement = FSChartsAxisPlacementSpec.Start,
        scale = FSChartsScaleSpec.Linear,
        tickDirection = FSChartsTickDirectionSpec.Outside,
        ticks = FSChartsTickSpec.Fixed(years),
        title = null
    )

    /**
     * The value axis's ticks: 0 up to the first multiple of a round step (1, 2, 2.5 or 5 times a power of ten) at
     * least [VALUE_AXIS_HEADROOM] above [highest], in at most [VALUE_AXIS_MAX_STEPS] steps. Empty when [highest] is
     * not a positive number.
     *
     * fs-charts' own nice domain and nice ticks disagree on short charts: the domain is rounded for about five ticks,
     * the ticks for as many as fit the plot's height, so on a phone the domain ends between two ticks, the top tick is
     * dropped and the highest value touches the plot's top edge. Fixing both here keeps them on the same step.
     */
    internal fun valueTicks(highest: Double?, wholeDollars: Boolean = false): List<Double> {
        if (highest == null || !(highest > 0.0) || !highest.isFinite()) {
            return emptyList()
        }
        val top = highest * (1 + VALUE_AXIS_HEADROOM)
        val magnitude = 10.0.pow(floor(log10(top / VALUE_AXIS_MAX_STEPS)))
        // A step of 10 × magnitude always fits: top / magnitude is below 10 × VALUE_AXIS_MAX_STEPS. With wholeDollars
        // (the hourly axis, labelled to the cent) a step is a whole number of dollars, so every label ends in .00.
        val step = (VALUE_AXIS_STEP_FACTORS + 10.0)
            .map { it * magnitude }
            .filter { !wholeDollars || (it >= 1.0 && it % 1.0 == 0.0) }
            .firstOrNull { ceil(top / it) <= VALUE_AXIS_MAX_STEPS }
            ?: 1.0
        return (0..ceil(top / step).toInt()).map { it * step }
    }

    // The highest value of the series shown, or of every series when all are hidden (as fs-charts keeps the axis then).
    private fun highestShown(model: WMBandChartModel, hiddenSeries: List<String>): Double? {
        val tops = listOf(
            WMBandChartSeries.P90 to model.points.mapNotNull { it.p90 },
            WMBandChartSeries.P75 to model.points.mapNotNull { it.p75 },
            WMBandChartSeries.MEDIAN to model.points.mapNotNull { it.median },
            WMBandChartSeries.P25 to model.points.mapNotNull { it.p25 },
            WMBandChartSeries.P10 to model.points.mapNotNull { it.p10 }
        )
        val shown = tops.filter { (name, _) -> name !in hiddenSeries }.flatMap { it.second }
        return shown.ifEmpty { tops.flatMap { it.second } }.maxOrNull()
    }

    private fun valueAxis(hourly: Boolean, ticks: List<Double>): FSChartsAxisSpec = FSChartsAxisSpec.kxs(
        domain = if (ticks.isEmpty()) {
            listOf(FSChartsDomainSpec.IncludeZero, FSChartsDomainSpec.Nice())
        } else {
            listOf(FSChartsDomainSpec.Fixed(min = ticks.first(), max = ticks.last()))
        },
        // fs-charts writes tooltip values with this formatter too, so hourly is to the cent ($23.45; ticks read $20.00).
        formatter = if (hourly) {
            FSChartsFormatterSpec.Currency(code = "USD", minFractionDigits = 2, maxFractionDigits = 2)
        } else {
            FSChartsFormatterSpec.Currency(code = "USD", minFractionDigits = 0, maxFractionDigits = 0)
        },
        grid = true,
        gridDashed = true,
        labelPosition = FSChartsAxisLabelPositionSpec.Outside,
        labelRotation = 0F,
        line = false,
        minorGrid = false,
        minorTicks = FSChartsTickSpec.None,
        overlap = FSChartsLabelOverlapSpec.Skip,
        placement = FSChartsAxisPlacementSpec.Start,
        scale = FSChartsScaleSpec.Linear,
        tickDirection = FSChartsTickDirectionSpec.None,
        ticks = if (ticks.isEmpty()) FSChartsTickSpec.Nice else FSChartsTickSpec.Fixed(ticks),
        title = null
    )
}

/** The space a band chart reserves; every size is the full width of its container. */
object WMBandChartSize {
    /**
     * 4:3, the mix builder's chart (Penpot builder boards), but never shorter than the phone chart height
     * ([WMBandChartAspectRatioStyle]): the chart's text keeps its size, so a 4:3 chart on a phone would leave the
     * plot a sliver between the ticks and a wrapped legend.
     */
    val standard: FSWasmEmbedSize get() = FSWasmEmbedSize.AspectRatio(4, 3)
    /** The chart height: the landing hero and the shared mix. */
    val fixedHeight: FSWasmEmbedSize get() = FSWasmEmbedSize.Height(WMChartHeightVar.value())
    /** The phone chart height: the auth brand panel and the shared mix on phones. */
    val compact: FSWasmEmbedSize get() = FSWasmEmbedSize.Height(WMChartHeightCompactVar.value())
    /** The shared mix's link-preview card: the card's full height less its padding. */
    val preview: FSWasmEmbedSize get() = FSWasmEmbedSize.Height(WMPreviewChartHeightVar.value())
}

/** The box [WMBandChartFrame] reserves; its height comes from the frame's size. */
val WMBandChartFrameStyle = CssStyle {
    base { Modifier.position(Position.Relative).fillMaxWidth() }
}

/**
 * The floor under a band chart sized by aspect ratio ([WMBandChartSize.standard]), on its frame and on the chart: the
 * phone chart height. It only takes effect below about 375px of width, where 4:3 is shorter than that.
 */
val WMBandChartAspectRatioStyle = CssStyle {
    base { Modifier.minHeight(WMChartHeightCompactVar.value()) }
}

/** Fills the frame and centres what it holds: the chart, or a loading indicator or message in its place. */
val WMBandChartFrameContentStyle = CssStyle {
    base {
        Modifier
            .position(Position.Absolute)
            .top(0.percent)
            .left(0.percent)
            .fillMaxSize()
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .textAlign(TextAlign.Center)
    }
}

/**
 * Reserves the space of a band chart of [size] ([WMBandChartSize]) and centres [content] in it, so the slot keeps
 * one size whether it holds the chart, a loading indicator or a message. Give the chart inside the same [size].
 */
@Composable
fun WMBandChartFrame(
    modifier: Modifier = Modifier,
    size: FSWasmEmbedSize = WMBandChartSize.standard,
    content: @Composable () -> Unit
) {
    val reserved = when (size) {
        is FSWasmEmbedSize.AspectRatio -> WMBandChartAspectRatioStyle.toModifier().aspectRatio(size.width, size.height)
        is FSWasmEmbedSize.Height -> Modifier.height(size.height)
    }
    Div(WMBandChartFrameStyle.toModifier().then(reserved).then(modifier).toAttrs()) {
        Div(WMBandChartFrameContentStyle.toModifier().toAttrs()) {
            content()
        }
    }
}

/**
 * Penpot `Wagemix / Band Chart`: the fscharts embed of [WMBandChartSpec], reserving [size]
 * ([WMBandChartSize]). [eager] loads the bundle immediately (the mix builder's hero chart),
 * otherwise it loads near the viewport. The chart draws on and interpolates between data sets
 * unless the reader prefers reduced motion. [description] is what crawlers and no-wasm readers see.
 */
@Composable
fun WMBandChart(
    model: WMBandChartModel,
    description: String,
    modifier: Modifier = Modifier,
    size: FSWasmEmbedSize = WMBandChartSize.standard,
    eager: Boolean = false,
    hiddenSeries: List<String> = emptyList(),
    onHiddenSeriesChange: (List<String>) -> Unit = {}
) {
    val animated = remember { !window.matchMedia(REDUCED_MOTION_QUERY).matches }
    val spec = remember(model, hiddenSeries, animated) { WMBandChartSpec.of(model, hiddenSeries = hiddenSeries, animated = animated) }
    val floor = if (size is FSWasmEmbedSize.AspectRatio) WMBandChartAspectRatioStyle.toModifier() else Modifier
    FSChartsChart(
        spec = spec,
        description = description,
        modifier = floor.then(modifier),
        size = size,
        loading = if (eager) FSWasmEmbedLoading.Eager else FSWasmEmbedLoading.Lazy,
        onHiddenSeriesChange = onHiddenSeriesChange
    )
}
